// Formatos da API /portal/* (docs/plans/processo-projetos/portal-cliente/README.md).

export interface ProcessoResumoLista {
    id: number,
    titulo: string,
    fluxo: string,
    situacao: string,
    percentual: number,
    inicio: string,
    previsao: string,
    fim: string | null,
}

export interface Evento {
    id: number,
    tipo: string,
    titulo: string,
    inicio: string,
    fim: string | null,
    local: string | null,
    situacao: string,
    /** O proprio cliente agendou pelo portal. */
    pelo_cliente?: boolean,
    participantes: string[],
}

export interface LinhaHistorico {
    quando: string,
    tipo: string,
    descricao: string,
    do_cliente: boolean,
    item_id: number | null,
}

export interface Resumo {
    id: number,
    titulo: string,
    cliente: string,
    consultor: string,
    situacao: string,
    percentual: number,
    etapas: {concluidas: number, total: number},
    inicio: string,
    previsao: string,
    fim: string | null,
    complexidade: string | null,
    dias_implantacao: number,
    etapa_atual: {id: number, nome: string, situacao: string} | null,
    proxima_etapa: {id: number, nome: string, situacao: string, descricao: string | null} | null,
    pendencias: {cliente: number, equipe: number},
    proximo_evento: Evento | null,
    esperamos_do_cliente: string[],
    responsaveis: string[],
    historico: LinhaHistorico[],
}

export interface Opcao {
    valor: string,
    rotulo: string,
    exige_observacao?: boolean,
}

/** Mensagem da conversa do item: duvida do cliente ou resposta da equipe. */
export interface MensagemItem {
    id: number,
    quando: string,
    autor: string,
    do_cliente: boolean,
    mensagem: string,
}

export interface Item {
    id: number,
    titulo: string,
    orientacao: string | null,
    acao_cliente: string | null,
    modulo: string | null,
    tipo: number,
    opcoes: Opcao[],
    observacao_regra: number,
    exige_anexo: boolean,
    situacao: string,
    em_revisao: boolean,
    responsavel: string,
    do_cliente: boolean,
    pode_responder: boolean,
    prazo: string,
    atrasado: boolean,
    resposta: any,
    observacao: string | null,
    justificativa_declinio: string | null,
    respondido_em: string | null,
    anexos: number,
    etapa?: any,
    prioridade?: string,
    conversa?: MensagemItem[],
    duvida_aberta?: boolean,
}

export interface EtapaTimeline {
    id: number,
    nome: string,
    situacao: string,
    descricao: string | null,
    responsavel: string,
    percentual: number,
    previsao_inicio: string,
    previsao_fim: string,
    data_inicio: string | null,
    data_fim: string | null,
    grupos: Array<{codigo: string | null, nome: string | null, itens: Item[]}>,
}

export interface ModuloTimeline {
    nome: string,
    codigo?: string | null,
    /** Escolhido no cadastro do modulo (#484909); null = pelo nome. */
    icone?: string | null,
    total: number,
    resolvidos: number,
    percentual: number,
    proximo: (Item & {etapa: string}) | null,
    itens: Array<Item & {etapa: string}>,
}

export interface Documento {
    id: number,
    nome: string,
    categoria: string,
    tamanho: number | null,
    data: string,
    autor: string,
    enviado_pelo_cliente: boolean,
    etapa: string | null,
    item_id: number | null,
}

export const TIPO = {CHECK: 1, SELECAO: 2, SELECAO_MULTIPLA: 3, TEXTO: 4, ARQUIVO: 5, DATA: 6};
export const OBS = {NAO: 0, OPCIONAL: 1, OBRIGATORIA: 2, CONFORME_OPCAO: 3};

export const SITUACAO_PROCESSO: {[s: string]: {rotulo: string, classe: string}} = {
    em_andamento: {rotulo: 'Em andamento', classe: 'andamento'},
    pausado: {rotulo: 'Pausado', classe: 'pausado'},
    encerrado: {rotulo: 'Encerrado', classe: 'encerrado'},
    concluido: {rotulo: 'Concluído', classe: 'concluido'},
};

export const SITUACAO_ETAPA: {[s: string]: string} = {
    concluida: 'Concluída',
    andamento: 'Em andamento',
    aguardando_cliente: 'Aguardando você',
    atrasada: 'Atrasada',
    nao_iniciada: 'Não iniciada',
};

export const SITUACAO_ITEM: {[s: string]: string} = {
    pendente: 'Pendente',
    em_andamento: 'Em andamento',
    aguardando_cliente: 'Aguardando você',
    concluido: 'Concluído',
    declinado: 'Não se aplica',
};

export const CATEGORIAS: Array<[string, string]> = [
    ['arquivo', 'Arquivo'],
    ['contrato', 'Contrato'],
    ['cronograma', 'Cronograma'],
    ['manual', 'Manual'],
    ['treinamento', 'Treinamento'],
    ['ata', 'Ata de reunião'],
    ['gravacao', 'Gravação'],
];

export const TIPO_EVENTO: {[t: string]: {rotulo: string, cor: string}} = {
    kickoff: {rotulo: 'Kick-off', cor: '#321fdb'},
    reuniao: {rotulo: 'Reunião', cor: '#0e7490'},
    treinamento: {rotulo: 'Treinamento', cor: '#7c3aed'},
    entrega: {rotulo: 'Entrega', cor: '#b45309'},
    golive: {rotulo: 'Go-Live', cor: '#16a34a'},
    outro: {rotulo: 'Outro', cor: '#6b7280'},
};

const doisDigitos = (n: number) => String(n).padStart(2, '0');

/** "2026-10-08" ou ISO -> "08/10/2026" */
export const data = (valor: string | null | undefined) => {
    if (!valor) return '—';
    const d = valor.length > 10 ? new Date(valor) : new Date(`${valor}T12:00:00`);
    return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const dataHora = (valor: string | null | undefined) => {
    if (!valor) return '—';
    const d = new Date(valor);
    return `${data(valor)} ${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
};

export const tamanho = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const textoResposta = (item: Item): string => {
    const r = item.resposta;
    if (r === null || r === undefined) return '';
    switch (item.tipo) {
        case TIPO.CHECK: return 'Feito';
        case TIPO.SELECAO: return item.opcoes.find(o => o.valor === r)?.rotulo ?? String(r);
        case TIPO.SELECAO_MULTIPLA: return (r as string[]).map(v => item.opcoes.find(o => o.valor === v)?.rotulo ?? v).join(', ');
        case TIPO.DATA: return data(r);
        default: return String(r);
    }
};
